import EmployeePositionForm from "./components/EmployeePositionForm";
import { Button, IconButton, TableCell } from "@material-ui/core";
import { Delete, Edit, Warning } from "@material-ui/icons";
import TableView from "@shared/components/Table/TableView";
import { ITableHeaderCell } from "@shared/components/Table/types/ITableHeaderCell";
import { useConfirmationDialog } from "@shared/context/ConfirmationDialogContext";
import {
  deleteEmployeePosition,
  getEmployeePositions,
} from "@shared/services/settings/employee-positions.service";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { FC, useCallback, useEffect, useState } from "react";

export interface IEmployeePositionType {
  id: number;
  name: string;
  createdOn: string;
  onEditClick?: (id: number) => void;
  onDeleteClick?: (id: number) => void;
}

const createEmployeePositionType = (
  id: number,
  name: string,
  createdOn: string,
  onEditClick?: (id: number) => void,
  onDeleteClick?: (id: number) => void
): IEmployeePositionType => ({
  id,
  name,
  createdOn,
  onEditClick,
  onDeleteClick,
});

const headerCells: ITableHeaderCell<IEmployeePositionType>[] = [
  { id: "id", label: "ID" },
  { id: "name", label: "Име" },
  {
    id: "createdOn",
    label: "Датум и време на додавање",
  },
  { id: "editButton", label: "", disableSorting: true },
  { id: "deleteButton", label: "", disableSorting: true },
];

const createRow = (row: IEmployeePositionType) => (
  <>
    <TableCell>{row.id}</TableCell>
    <TableCell>{row.name}</TableCell>
    <TableCell>{dayjs.utc(row.createdOn).local().format("LLL")}</TableCell>
    <TableCell padding="checkbox">
      <IconButton size="small" onClick={() => (row.onEditClick ? row.onEditClick(row.id) : null)}>
        <Edit />
      </IconButton>
    </TableCell>
    <TableCell padding="checkbox">
      <IconButton
        size="small"
        onClick={() => (row.onDeleteClick ? row.onDeleteClick(row.id) : null)}
      >
        <Delete />
      </IconButton>
    </TableCell>
  </>
);

const getAllEmployeePositionTypes = () => getEmployeePositions();

const EmployeePositionViewPage: FC = () => {
  const {
    data: employeePositionTypes,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery(["employeePositionTypes"], getAllEmployeePositionTypes, {
    refetchInterval: 15000,
  });

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  useEffect(() => {
    if (!error || !(error instanceof Error)) return;

    enqueueSnackbar(`Се случи грешка, обидете се повторно. ${error.message}`, {
      variant: "error",
      persist: true,
      preventDuplicate: true,
      anchorOrigin: { horizontal: "center", vertical: "bottom" },
      action: function onDismiss(key) {
        return <Button onClick={() => closeSnackbar(key)}>OK</Button>;
      },
    });
  }, [error, enqueueSnackbar, closeSnackbar]);

  const confirmationDialog = useConfirmationDialog();

  const handleDeleteClick = (id: number) => {
    const employeePositionType = employeePositionTypes.find(
      (x: IEmployeePositionType) => x.id === id
    );

    if (employeePositionType) {
      confirmationDialog.open({
        icon: <Warning fontSize="large" style={{ color: "orange" }} />,
        title: "Избриши позиција",
        body: `Дали сте сигурни дека сакате да јо избришете позицијата ${employeePositionType.name} со ID ${employeePositionType.id}?`,
        onConfirm: () => handleDelete(employeePositionType.id),
        onDeny: () => confirmationDialog.close(),
      });
    }
  };

  const handleDelete = useCallback(
    (id: number) => {
      confirmationDialog.setLoading(true);

      deleteEmployeePosition(id)
        .then(() => {
          confirmationDialog.close();
          enqueueSnackbar("Успешно ја избришавте позицијата", {
            variant: "success",
            autoHideDuration: 6000,
          });

          refetch();
        })
        .catch((error: AxiosError) => {
          confirmationDialog.close();
          enqueueSnackbar(`Не успеа да се отстрани позицијата: ${error.message}`, {
            variant: "error",
            persist: true,
            preventDuplicate: true,
            anchorOrigin: { horizontal: "center", vertical: "bottom" },
            action: function onDismiss(key) {
              return <Button onClick={() => closeSnackbar(key)}>OK</Button>;
            },
          });
        });
    },
    [closeSnackbar, enqueueSnackbar, refetch]
  );

  const handleEditClick = (id: number) => {
    const employeePositionType = employeePositionTypes.find(
      (x: IEmployeePositionType) => x.id === id
    );

    if (employeePositionType) setSelectedRow(employeePositionType);
  };

  const [selectedRow, setSelectedRow] = useState<IEmployeePositionType | null>(null);
  const handleAddTypeClick = () => {
    setSelectedRow(createEmployeePositionType(0, "", ""));
  };

  const handleAddTypeDialogClose = () => {
    setSelectedRow(null);
  };

  // Map data
  const rows: IEmployeePositionType[] = [];
  if (employeePositionTypes && employeePositionTypes) {
    employeePositionTypes.forEach((x: IEmployeePositionType) => {
      rows.push(
        createEmployeePositionType(x.id, x.name, x.createdOn, handleEditClick, handleDeleteClick)
      );
    });
  }

  return (
    <TableView
      title="Работна позиција"
      addButtonText="Додади позиција"
      onAddButtonClick={handleAddTypeClick}
      onRefreshClick={() => refetch()}
      headers={headerCells}
      rows={rows}
      render={createRow}
      isLoading={isLoading || isFetching}
      addDialog={
        <EmployeePositionForm
          title={selectedRow?.id ? "Промени позиција" : "Додади позиција"}
          addButtonText={selectedRow?.id ? "Зачувај" : "Додади"}
          open={selectedRow ? true : false}
          row={selectedRow?.id ? selectedRow : null}
          onClose={handleAddTypeDialogClose}
          onSuccess={() => refetch()}
        />
      }
    />
  );
};

export default EmployeePositionViewPage;
